import React, { useRef, useState } from "react";

import { Button, Col, FormInstance, Row, Space } from "antd";
import { toast } from "utils/toast";

import { useCreateLandlord, useUpdateLandlord } from "hooks/useLandlords";

import { LandlordForm } from "js/components/Landlords/LandlordForm/LandlordForm";
import LandlordListSelect from "js/components/Landlords/LandlordListSelect/LandlordListSelect";
import { LandlordTabProps } from "js/components/Properties/types";
import { Landlord } from "js/components/types";

const LandlordTab: React.FC<LandlordTabProps> = ({
  selectedLandlord = {},
  setSelectedLandlord,
  isEditView,
  isCreateView,
  setIsEditView,
  setIsCreateView,
}) => {
  const landlordFormRef = useRef<FormInstance>(null);

  // Create landlord mutation
  const createLandlordMutation = useCreateLandlord({
    onSuccess: (landlordValues) => {
      setSelectedLandlord(landlordValues);
      setIsCreateView(false);
      toast.success("Propietario creado correctamente.");
    },
    onError: () => {
      toast.error("Error al crear el propietario.");
    },
  });

  // Update landlord mutation
  const updateLandlordMutation = useUpdateLandlord({
    onSuccess: (landlordValues) => {
      setSelectedLandlord(landlordValues);
      setIsEditView(false);
      toast.success("Propietario actualizado correctamente.");
    },
    onError: () => {
      toast.error("Error al actualizar el propietario.");
    },
  });

  // Combined loading state from mutations
  const loading =
    createLandlordMutation.isPending || updateLandlordMutation.isPending;

  const normalizeString = (value: string): string => {
    if (value === "") {
      return value;
    }
    return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };

  const validateFields = async () => {
    await landlordFormRef.current.validateFields();
  };

  const handleSaveOrUpdateLandlord = (values: any, isLegalPerson: boolean) => {
    const sanitizeOptionalField = (fieldValue?: string | null) =>
      fieldValue === undefined || fieldValue === null
        ? ""
        : normalizeString(fieldValue);

    const landlord: Landlord = {
      name: normalizeString(values.name),
      first_surname: normalizeString(values.first_surname),
      email: normalizeString(values.email),
      phone_number: values.phone_number,
      dial_code: values.dial_code ?? "+52",
      legal_type: isLegalPerson ? 2 : 1,
      second_surname: sanitizeOptionalField(values.second_surname),
      legal_entity_name: isLegalPerson
        ? sanitizeOptionalField(values.legal_entity_name)
        : "",
    };

    if (isEditView) {
      const mergedLandlord: Landlord = {
        ...(selectedLandlord as Landlord),
        ...landlord,
      };
      updateLandlordMutation.mutate({
        landlord: mergedLandlord,
        landlordId: selectedLandlord.id,
      });
    } else if (isCreateView) {
      createLandlordMutation.mutate(landlord);
    }
  };

  return (
    <>
      <Row style={{ width: "100%", marginBottom: 10, marginRight: 25 }}>
        <Col xs={24} sm={24} md={24} lg={24}>
          <Space
            direction="horizontal"
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "end",
            }}
          >
            {Object.keys(selectedLandlord).length > 0 &&
              !isEditView &&
              !isCreateView && (
                <Button
                  loading={loading}
                  type="primary"
                  onClick={() => {
                    if (isEditView) {
                      setIsEditView(false);
                    }
                    setIsEditView(!isEditView);
                    setIsCreateView(false);
                  }}
                >
                  Editar Información
                </Button>
              )}
            {!isEditView && !isCreateView && (
              <Button
                loading={loading}
                type="primary"
                onClick={() => {
                  setIsCreateView(!isCreateView);
                  setIsEditView(false);
                }}
              >
                Registrar Propietario
              </Button>
            )}
          </Space>
        </Col>
      </Row>
      <Row style={{ width: "100%" }}>
        <Col xs={24} sm={24} md={24} lg={24}>
          {(isEditView || isCreateView) && (
            <LandlordForm
              ref={landlordFormRef}
              legalTypeValue={selectedLandlord?.legal_type === 1 ? "pf" : "pm"}
              handleSave={handleSaveOrUpdateLandlord}
              {...(isEditView ? { selectedLandlord } : {})}
            />
          )}
          {!isEditView && !isCreateView && (
            <LandlordListSelect
              selectedLandlord={selectedLandlord}
              setSelectedLandlord={setSelectedLandlord}
            />
          )}
        </Col>
        {(isEditView || isCreateView) && (
          <Row style={{ width: "100%", marginTop: 10 }}>
            <Col
              xs={12}
              sm={12}
              md={12}
              lg={12}
              xl={12}
              style={{ textAlign: "center" }}
            >
              <Button
                loading={loading}
                onClick={() => {
                  setIsCreateView(false);
                  setIsEditView(false);
                }}
              >
                Cancelar
              </Button>
            </Col>
            <Col
              xs={12}
              sm={12}
              md={12}
              lg={12}
              xl={12}
              style={{ textAlign: "center" }}
            >
              <Button loading={loading} type="primary" onClick={validateFields}>
                {isEditView ? "Guardar Cambios" : "Registrar"}
              </Button>
            </Col>
          </Row>
        )}
      </Row>
    </>
  );
};

export default LandlordTab;
